import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Megaphone, Plus, Pencil, Trash2, Pin } from 'lucide-react';
import { RichTextEditor, HtmlView } from '../RichTextEditor';
import { fmtDateTime } from '../../lib/utils';
import { toast } from 'sonner';

const EMPTY = { title: '', body: '', priority: 'normal', pinned: false, expires_at: '' };
const PRIORITY_STYLE = {
  urgent: 'bg-rose-100 text-rose-700',
  important: 'bg-amber-100 text-amber-700',
  normal: 'bg-slate-100 text-slate-600',
};

export function AnnouncementsPanel() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get('/announcements').then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    const payload = { ...form, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null };
    try {
      if (form.id) await api.put(`/announcements/${form.id}`, payload);
      else await api.post('/announcements', payload);
      toast.success(form.id ? 'Announcement updated' : 'Announcement posted — team notified');
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete announcement "${a.title}"?`)) return;
    await api.delete(`/announcements/${a.id}`);
    toast.success('Announcement deleted');
    load();
  };

  if (items === null) return null;
  if (items.length === 0 && !isAdmin) return null;

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="announcements-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-magenta" />
          <h3 className="font-heading font-bold text-navy">Announcements</h3>
        </div>
        {isAdmin && (
          <Button size="sm" className="bg-magenta hover:bg-magenta/90" onClick={() => setForm({ ...EMPTY })} data-testid="new-announcement-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Announcement
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No announcements yet — post one to keep the whole team in the loop.</p>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const long = (a.body || '').length > 240;
            const open = expanded[a.id];
            return (
              <div key={a.id} className={`rounded-xl border p-4 ${a.pinned ? 'border-magenta/30 bg-magenta/[0.03]' : 'border-slate-100'}`} data-testid={`announcement-${a.id}`}>
                <div className="flex items-start gap-2 flex-wrap">
                  {a.pinned && <Pin className="w-3.5 h-3.5 text-magenta mt-1 shrink-0" />}
                  <span className="font-semibold text-navy text-sm">{a.title}</span>
                  {a.priority !== 'normal' && (
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${PRIORITY_STYLE[a.priority]}`}>{a.priority}</span>
                  )}
                  <span className="text-[11px] text-slate-400 ml-auto shrink-0">{a.created_by_name} · {fmtDateTime(a.created_at)}</span>
                  {isAdmin && (
                    <span className="flex gap-1 shrink-0">
                      <button onClick={() => setForm({ ...a, expires_at: a.expires_at ? a.expires_at.slice(0, 10) : '' })} className="p-1 text-slate-400 hover:text-navy" data-testid={`edit-announcement-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(a)} className="p-1 text-rose-400 hover:text-rose-600" data-testid={`delete-announcement-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                  )}
                </div>
                {a.body && (
                  <div className="mt-1.5 text-sm text-slate-600">
                    <div className={long && !open ? 'max-h-20 overflow-hidden relative' : ''}>
                      <HtmlView html={a.body} testId={`announcement-body-${a.id}`} />
                    </div>
                    {long && (
                      <button onClick={() => setExpanded({ ...expanded, [a.id]: !open })} className="text-xs font-semibold text-magenta hover:underline mt-1" data-testid={`announcement-toggle-${a.id}`}>
                        {open ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <Dialog open onOpenChange={(v) => !v && setForm(null)}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="announcement-dialog">
            <DialogHeader><DialogTitle>{form.id ? 'Edit' : 'New'} Announcement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q3 Partnership Drive kicks off Monday" data-testid="announcement-title-input" />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} minHeight={120} label="Announcement" testId="announcement-body-editor" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="announcement-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="important">Important</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Auto-expire on (optional)</Label>
                  <Input type="date" value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="announcement-expires" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="announcement-pinned" />
                Pin to top
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button className="bg-magenta hover:bg-magenta/90" onClick={save} data-testid="save-announcement-btn">{form.id ? 'Update' : 'Post & Notify Team'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
